import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { DebatesService, Debate, CreateDebateData } from '@/services/social/debatesService';
import { toast } from 'sonner';

export const useDebates = (interviewId: string) => {
  const queryClient = useQueryClient();

  const {
    data: debates = [],
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['debates', interviewId],
    queryFn: () => DebatesService.getDebatesForInterview(interviewId),
    enabled: !!interviewId,
  });

  const createDebateMutation = useMutation({
    mutationFn: (data: CreateDebateData) => DebatesService.createDebate(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['debates', interviewId] });
      toast.success('Debatt opprettet');
    },
    onError: (error) => {
      console.error('Error creating debate:', error);
      toast.error('Kunne ikke opprette debatt');
    },
  });

  const addArgumentMutation = useMutation({
    mutationFn: ({ debateId, argumentText, anonymousName }: { debateId: string; argumentText: string; anonymousName?: string }) => 
      DebatesService.addArgument(debateId, argumentText, anonymousName),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['debates', interviewId] });
      toast.success('Argument lagt til');
    },
    onError: (error) => {
      console.error('Error adding argument:', error);
      toast.error('Kunne ikke legge til argument');
    },
  });

  const voteMutation = useMutation({
    mutationFn: ({ debateId, votedForUserId }: { debateId: string; votedForUserId: string }) => 
      DebatesService.voteForDebate(debateId, votedForUserId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['debates', interviewId] });
      toast.success('Stemme registrert');
    },
    onError: (error) => {
      console.error('Error voting:', error);
      toast.error('Kunne ikke registrere stemme');
    },
  });

  const updateStatusMutation = useMutation({
    mutationFn: ({ debateId, status }: { debateId: string; status: Debate['status'] }) => 
      DebatesService.updateDebateStatus(debateId, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['debates', interviewId] });
      toast.success('Debatt-status oppdatert');
    },
    onError: (error) => {
      console.error('Error updating debate status:', error);
      toast.error('Kunne ikke oppdatere debatt-status');
    },
  });

  return {
    debates,
    isLoading,
    error,
    refetch,
    createDebate: createDebateMutation.mutate,
    addArgument: (debateId: string, argumentText: string, anonymousName?: string) =>
      addArgumentMutation.mutate({ debateId, argumentText, anonymousName }),
    addArgumentAsync: (debateId: string, argumentText: string, anonymousName?: string) =>
      addArgumentMutation.mutateAsync({ debateId, argumentText, anonymousName }),
    vote: (debateId: string, votedForUserId: string) => 
      voteMutation.mutate({ debateId, votedForUserId }),
    updateStatus: (debateId: string, status: Debate['status']) => 
      updateStatusMutation.mutate({ debateId, status }),
    isCreatingDebate: createDebateMutation.isPending,
    isAddingArgument: addArgumentMutation.isPending,
    isVoting: voteMutation.isPending,
    isUpdatingStatus: updateStatusMutation.isPending,
  };
};